import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtCurrency } from "@/lib/utils-app";
import { CreditCard, Landmark, Loader2 } from "lucide-react";

export default function SubscriptionPaymentPanel({ subscription, onUpdated, compact = false }) {
  const [mpLoading, setMpLoading] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [proofNote, setProofNote] = useState("");

  if (!subscription) return null;

  const interval = subscription.billing_interval || "monthly";
  const amount = interval === "annual" ? subscription.annual_amount : subscription.monthly_amount;
  const intervalLabel = interval === "annual" ? "anual" : "mensual";

  const payWithMp = async () => {
    setMpLoading(true);
    try {
      const { data } = await api.post("/admin/subscription/checkout/mp");
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error("No se recibió URL de pago");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al iniciar pago con Mercado Pago");
    } finally {
      setMpLoading(false);
    }
  };

  const reportTransfer = async () => {
    setTransferLoading(true);
    try {
      await api.post("/admin/subscription/checkout/transfer", { proof_note: proofNote });
      toast.success("Transferencia reportada. El equipo de DanySolutions la revisará.");
      setProofNote("");
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo reportar la transferencia");
    } finally {
      setTransferLoading(false);
    }
  };

  const wrapperClass = compact
    ? "rounded-sm border border-white/10 bg-white/[0.02] p-4 space-y-3"
    : "glass-strong p-6 rounded-sm space-y-4 max-w-lg w-full";

  return (
    <div className={wrapperClass} data-testid="subscription-payment-panel">
      {subscription.pending_transfer && (
        <p className="text-xs text-amber-400/90 border border-amber-500/30 rounded-sm px-3 py-2">
          Tienes una transferencia en revisión. Te avisaremos cuando se confirme.
        </p>
      )}
      <div className="text-sm text-white/70">
        Renovación {intervalLabel}:{" "}
        <span className="text-[#FFD700] font-semibold">{fmtCurrency(amount || 0)}</span>
      </div>
      {subscription.platform_mp_configured && (
        <button
          type="button"
          onClick={payWithMp}
          disabled={mpLoading}
          className="btn-primary w-full justify-center text-sm py-2.5"
          data-testid="subscription-pay-mp-btn"
        >
          {mpLoading ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
          Pagar con Mercado Pago
        </button>
      )}
      {(subscription.transfer_instructions || "").trim() && (
        <div className="space-y-2 border-t border-white/10 pt-3">
          <div className="flex items-center gap-2 text-xs text-white/50 uppercase tracking-wider">
            <Landmark size={14} /> Transferencia
          </div>
          <pre className="text-xs text-white/60 whitespace-pre-wrap font-sans leading-relaxed">
            {subscription.transfer_instructions}
          </pre>
          <textarea
            className="input-dark w-full text-xs min-h-[72px]"
            placeholder="Referencia o nota opcional (folio, hora, banco…)"
            value={proofNote}
            onChange={(e) => setProofNote(e.target.value)}
          />
          <button
            type="button"
            onClick={reportTransfer}
            disabled={transferLoading || subscription.pending_transfer}
            className="btn-outline w-full justify-center text-xs py-2"
            data-testid="subscription-report-transfer-btn"
          >
            {transferLoading ? <Loader2 className="animate-spin" size={14} /> : null}
            Reportar transferencia
          </button>
        </div>
      )}
      {!subscription.platform_mp_configured && !(subscription.transfer_instructions || "").trim() && (
        <p className="text-xs text-white/50">Contacta a DanySolutions para configurar métodos de pago.</p>
      )}
    </div>
  );
}
